"use client";

import CustomButton from "@/components/ui/custom/CustomButton";
import { GeneralContactForm } from "@/components/ui/form/contact/GeneralContactForm";
import { ShadEmailForm } from "@/components/ui/form/mailChimp/ShadEmailForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, } from "@/components/ui/shadcn/dialog";
import { useDialog } from '@/contexts/DialogContext';
import { cn } from "@/lib/utils";

/**
 * Button configuration interface
 */
export interface ContactButton {
    /** Button text */
    text: string;
    /** Link href (for direct links) - mutually exclusive with onClick and dialog */
    href?: string;
    /** Whether to open link in new tab */
    isBlank?: boolean;
    /** Show arrow icon */
    showArrow?: boolean;
    /** Icon name (e.g., "CalendarPlus", "Mail") - resolved in CustomButton */
    iconName?: string;
    /** Custom className */
    className?: string;
    /** Dialog to open (e.g., "email", "booking") - triggers modal */
    dialog?: string;
    /** Custom onClick handler - mutually exclusive with href and dialog */
    onClick?: () => void;
}

interface ContactSectionProps {
    /** Custom buttons to display. If not provided, uses default buttons */
    buttons?: ContactButton[];
    /** Optional custom className for the container */
    className?: string;
}

export default function ContactSection({ buttons, className }: ContactSectionProps) {

    const { activeDialog, setActiveDialog } = useDialog();

    const useBookingIframe = false;

    // Default buttons if none provided
    const defaultButtons: ContactButton[] = [
        {
            text: "Email Sign Up",
            dialog: "email",
            className: "bg-red-600 hover:bg-red-700 text-white"
        },
        {
            text: "Want to perform?",
            href: "https://docs.google.com/forms/d/e/1FAIpQLSeac72xR7rzwFd7mW0QcKrnt-vpKfmh2vPxMGIK9r70UtwHcA/viewform?pli=1",
            isBlank: true,
            className: cn("bg-red-600 hover:bg-red-700 text-white", "w-full"),
            showArrow: false
        }
    ];

    const displayButtons = buttons || defaultButtons;

    return (
        <div className={cn("mt-20 w-full", className)}>
            <div className="container mx-auto px-6 md:px-0">
                {/*
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Contact Us</h2>
                <p className="text-lg text-center max-w-2xl mx-auto mb-12">
                    Have questions about our comedy events or interested in booking a comedian for your next event? Get
                    in touch with us!
                </p>
                */}

                <div className="flex flex-col md:flex-row gap-4 justify-center max-w-3xl mx-auto items-center md:items-stretch">
                    {displayButtons.map((button, index) => {
                        // Handle dialog trigger
                        if (button.dialog) {
                            return (
                                <CustomButton
                                    key={index}
                                    onClick={() => setActiveDialog(button.dialog!)}
                                    className={button.className}
                                    showArrow={button.showArrow}
                                    iconName={button.iconName}
                                >
                                    {button.text}
                                </CustomButton>
                            );
                        }

                        // Handle custom onClick
                        if (button.onClick) {
                            return (
                                <CustomButton
                                    key={index}
                                    onClick={button.onClick}
                                    className={button.className}
                                    showArrow={button.showArrow}
                                    iconName={button.iconName}
                                >
                                    {button.text}
                                </CustomButton>
                            );
                        }

                        // Handle href link
                        return (
                            <CustomButton
                                key={index}
                                href={button.href}
                                isBlank={button.isBlank}
                                text={button.text}
                                className={button.className}
                                showArrow={button.showArrow}
                                iconName={button.iconName}
                            />
                        );
                    })}
                </div>
                {/*
                   <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6")}>
            {sampleLinks.map((link, idx) => (
                <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    // onClick={() => trackClick(link)}
                >
                    <div
                        className="bg-background p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center space-x-4">
                         Icon
                         TODO: Make icon variable
                        <div
                            className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            {link.icon && (<link.icon className="h-5 w-5"/>)}
                        </div>
                         Link Info
                        <div>
                            <h3 className="text-lg font-semibold text-foreground">{link.title}</h3>
                            <p className="text-sm text-muted-foreground">{link.description}</p>
                        </div>
                    </div>
                </a>
            ))}
        </div>*/}

                {/* Email Signup Dialog */}
                <Dialog open={activeDialog === "email"} onOpenChange={() => setActiveDialog(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Email List</DialogTitle>
                            <DialogDescription>
                                Sign Up for email updates
                            </DialogDescription>
                        </DialogHeader>

                        <ShadEmailForm onSubmitCallback={() => {
                            setActiveDialog(null)
                        }}/>
                    </DialogContent>
                </Dialog>

                {/* General Contact Dialog */}
                <Dialog open={activeDialog === "general"} onOpenChange={() => setActiveDialog(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>General Contact</DialogTitle>
                            <DialogDescription>
                                Have a question or comment? We&apos;d love to hear from you!
                            </DialogDescription>
                        </DialogHeader>
                        <GeneralContactForm onSubmit={() => setActiveDialog(null)}/>
                    </DialogContent>
                </Dialog>

                {/* Booking Request Dialog */}
                <Dialog open={activeDialog === "booking"} onOpenChange={() => setActiveDialog(null)}>
                    <DialogContent className="sm:max-w-xl">
                        <DialogHeader>
                            <DialogTitle>Comedian Booking Request</DialogTitle>
                            <DialogDescription>
                                Apply to be on our show!
                            </DialogDescription>
                        </DialogHeader>


                        <div className="min-h-80">
                            <iframe
                                src="https://docs.google.com/forms/d/e/1FAIpQLSeac72xR7rzwFd7mW0QcKrnt-vpKfmh2vPxMGIK9r70UtwHcA/viewform?embedded=true"
                                width="100%" height="100%">
                                Loading…
                            </iframe>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
